import { z } from "zod";

import { databaseDefaultsShape, DatabaseDefaults } from "@/src/models/database-defaults";

import type { toAISdkV5Messages } from "@mastra/ai-sdk/ui";

export enum AgentRunStatus {
  QUEUED = "queued",
  PROCESSING = "processing",
  MATERIALIZING = "materializing",
  PAUSED = "paused",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum AgentStepStatus {
  COMPLETED = "completed",
  FAILED = "failed",
  ESCALATED = "escalated",
}

export enum FailureReason {
  INVALID_INPUT = "invalid_input",
  EXHAUSTED = "exhausted",
  CRASHED = "crashed",
}

export const agentRunSchema = z.object({
  agentId: z.string(),
  // Dual role: Mastra Memory thread key AND the dedup/idempotency token (unique among live rows).
  threadId: z.string(),
  model: z.string().nullable(),
  status: z.nativeEnum(AgentRunStatus),
  result: z.unknown().nullable().optional(),
  metadata: z.unknown().nullable().optional(),
  failureReason: z.nativeEnum(FailureReason).nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});

export type AgentRun = z.infer<typeof agentRunSchema> & DatabaseDefaults;

export function isManualIntakeBypassRun(agentRun: AgentRun | null | undefined): boolean {
  return Boolean(
    agentRun?.metadata &&
    typeof agentRun.metadata === "object" &&
    "manualIntakeBypass" in agentRun.metadata &&
    agentRun.metadata.manualIntakeBypass === true
  );
}

export const agentRunShape: AgentRun = {
  ...databaseDefaultsShape,
  agentId: "",
  threadId: "",
  model: null,
  status: AgentRunStatus.QUEUED,
  result: null,
  metadata: null,
  failureReason: null,
  startedAt: null,
  completedAt: null,
};

export const agentStepSchema = z.object({
  agentRunId: z.string(),
  taskId: z.string(),
  stepKey: z.string(),
  status: z.nativeEnum(AgentStepStatus),
  output: z.unknown().nullable().optional(),
});

export type AgentStep = z.infer<typeof agentStepSchema> & DatabaseDefaults;

export type AgentMessage = ReturnType<typeof toAISdkV5Messages>[number];

// "processing" is UI-only, not a persisted AgentStepStatus: it means the run is actively working and
// hasn't written this step's row yet. null means nothing is currently happening for this step.
export type DerivedStepStatus = AgentStepStatus | "processing" | null;
